import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import {
    InteractionRequiredAuthError,
    InteractionStatus,
    type AccountInfo,
    type PublicClientApplication,
} from '@azure/msal-browser';
import { useMsal, useIsAuthenticated, MsalProvider } from '@azure/msal-react';

import { useAppConfig } from '@/config/AppConfigContext';
import { AuthContext } from './AuthContext';
import type { AuthContextValue, AuthUser } from './AuthContext';
import { buildLoginRequest, buildTokenRequest } from './msalConfig';

// ─── Live MSAL context bridge ─────────────────────────────────────────────────

/**
 * Reads MSAL state from the hooks provided by MsalProvider and exposes it
 * through AuthContext so components only depend on AuthContext, not MSAL directly.
 *
 * This component must be rendered inside a <MsalProvider>.
 */
function MsalAuthBridge({ children }: { children: ReactNode }) {
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const config = useAppConfig();

    const isLoading = inProgress !== InteractionStatus.None;

    const activeAccount: AccountInfo | null = instance.getActiveAccount();

    const signIn = useCallback(() => {
        void instance.loginRedirect(buildLoginRequest(config));
    }, [instance, config]);

    const signOut = useCallback(() => {
        void instance.logoutRedirect({
            account: activeAccount ?? undefined,
        });
    }, [instance, activeAccount]);

    const getAccessToken = useCallback(async (): Promise<string> => {
        const account = activeAccount;
        if (account === null) {
            // No account in cache — start a fresh login redirect
            await instance.loginRedirect(buildLoginRequest(config));
            throw new Error('Redirecting to login — no cached account');
        }

        try {
            const result = await instance.acquireTokenSilent({
                ...buildTokenRequest(config),
                account,
            });
            return result.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // Token cannot be acquired silently (expired, revoked, consent needed)
                // Fall back to an interactive redirect
                await instance.acquireTokenRedirect({
                    ...buildTokenRequest(config),
                    account,
                });
                throw new Error('Redirecting for token refresh', { cause: error });
            }
            throw error;
        }
    }, [instance, activeAccount, config]);

    const value = useMemo<AuthContextValue>(() => {
        const user: AuthUser | null = activeAccount
            ? {
                  name: activeAccount.name ?? activeAccount.username,
                  email: activeAccount.username,
                  accountId: activeAccount.homeAccountId,
              }
            : null;
        return { isAuthenticated, isLoading, user, signIn, signOut, getAccessToken };
    }, [isAuthenticated, isLoading, activeAccount, signIn, signOut, getAccessToken]);

    return <AuthContext value={value}>{children}</AuthContext>;
}

// ─── Public AuthProvider ──────────────────────────────────────────────────────

interface AuthProviderProps {
    msalInstance: PublicClientApplication;
    children: ReactNode;
}

/**
 * Wraps the application with Microsoft Entra ID authentication via MSAL React.
 * Place this below AppConfigProvider and above all components that call useAuth().
 */
export function AuthProvider({ msalInstance, children }: AuthProviderProps) {
    return (
        <MsalProvider instance={msalInstance}>
            <MsalAuthBridge>{children}</MsalAuthBridge>
        </MsalProvider>
    );
}
